import axios, { AxiosRequestConfig } from "axios";
import React, { useContext, useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { useParams, useLocation } from "react-router-dom";
import {
  templateActionEnum,
  DOCUMENT_PAPER_TYPE,
  formActionEnum,
} from "../../Config/Constants";
import TemplateDataContext from "../../Contexts/TemplateData.context";
import { logger } from "../../Middleware/Logger";
import { getTemplateHtml } from "../../Services/TemplateServices";
import ErrorMessage from "../../Util/ErrorMessage";
import Template from "../Template/Template";
import LoaderComponent from "./LoaderComponent";
import ModalTitle from "./ModalTitle";
import FormDataContext from "../../Contexts/FormData.context";

type Props = {};

const ModalComponent = (props: Props) => {
  const { state, dispatch } = useContext(TemplateDataContext);
  const formStateData = useContext(FormDataContext);
  const {
    showTemplateModal,
    templates,
    templatePath,
    selectedState,
    isLoading,
  } = state;
  const { template } = useParams();
  const location = useLocation();
  const [title, setTitle] = useState<string>("");
  const [isModalClass, setIsModalClass] = useState<string>("letter_paper");
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [svgData, setSvgData] = useState<string[]>([]);
  const modalContentRef = React.useRef<HTMLDivElement | null>(null);
  // Force remount for reliable refresh similar to review summary thumbnail logic
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const lastLoadedTemplateRef = useRef<string | undefined>(undefined);
  const invalidatedRef = useRef<boolean>(false); // marks that we navigated away from a template route

  const loadPaperTypeContent = async ({
    html,
    paperType,
  }: {
    html?: string;
    paperType: string;
  }) => {
    dispatch({ type: templateActionEnum.SET_IS_LOADING, payload: true });
    const letterSize = paperType;
    const getHtmlData: any = document.getElementById("getHtml");
    try {
      const snapshotHtml = getHtmlData?.innerHTML || html || ""; // fallback to provided html to avoid blank
      if (snapshotHtml) {
        setHtmlContent(snapshotHtml);
      }
      const formData = new FormData();
      formData.append("html", snapshotHtml);
      formData.append("paperSize", letterSize);
      // Potential svg rendering disabled for now
      dispatch({ type: templateActionEnum.SET_IS_LOADING, payload: false });
      formStateData.dispatch({
        type: formActionEnum.SET_DOC_STYLE,
        payload: { letterSize },
      });
    } catch (error) {
      if (!axios.isCancel(error)) {
        logger.error(
          "🚀 ~ file: useEffect.tsx ~ line 47 ~ changeLayout ~ error",
          error
        );
        dispatch({
          type: templateActionEnum.SET_ERROR,
          payload: { show: true, message: ErrorMessage.TEMPLATE_HTML_FAILED },
        });
      }
      dispatch({ type: templateActionEnum.SET_IS_LOADING, payload: false });
    }
  };

  /* Fetch the template content and set title */
  const getTemplate = async (config: AxiosRequestConfig) => {
    if (
      template !== undefined &&
      templates[template] !== undefined &&
      templates[template].title !== undefined
    ) {
      setTitle(templates[template].title);
    }
    if (!template) return;
    // Avoid redundant fetch if same template already loaded & we still have content
  if (!invalidatedRef.current && lastLoadedTemplateRef.current === template && htmlContent) return;
    const { data: html } = await getTemplateHtml({
      endPoint: templatePath,
      templateKey: template || "",
      config,
      state: selectedState,
    });

    if (html) {
      setHtmlContent(html);
      lastLoadedTemplateRef.current = template;
      await loadPaperTypeContent({
        html,
        paperType: DOCUMENT_PAPER_TYPE[0].val,
      });
    } else {
      lastLoadedTemplateRef.current = undefined;
    }
    /* select the title from loaded templated */
    const matchingTitle = document.querySelector(".thumb-title");
    if (matchingTitle && matchingTitle.textContent) {
      setTitle(matchingTitle.textContent.trim());
    } else {
      /* if title is not empty then fetch from template */
      /* replace all the '\r\n'  */
      /* match the div with class 'thumb-title' and get the title  */
      const matching: string[] | null = html
        .replaceAll("\r\n", "")
        .match(/<div\sclass="[^"]*?thumb-title[^"]*?"[\r\n]?.*?>(.*?)<\/div>/);

      if (matching) {
        matching.forEach((element: string, index: number) => {
          if (index === 1) setTitle(element);
        });
      }
    }
  };

  const changeLayout = async (e: any): Promise<void> => {
    dispatch({ type: templateActionEnum.SET_IS_LOADING, payload: true });
    if (e.target.value === "legal") {
      setIsModalClass("legal_paper");
    } else {
      setIsModalClass("letter_paper");
    }
    try {
      const paperType = e.target.value;
      dispatch({ type: templateActionEnum.SET_IS_LOADING, payload: true });
      await loadPaperTypeContent({ paperType });
    } catch (error) {
      if (!axios.isCancel(error)) {
        logger.error(
          "🚀 ~ file: useEffect.tsx ~ line 47 ~ getTemplate ~ error",
          error
        );
        dispatch({
          type: templateActionEnum.SET_ERROR,
          payload: { show: true, message: ErrorMessage.TEMPLATE_HTML_FAILED },
        });
      }
    } finally {
      dispatch({ type: templateActionEnum.SET_IS_LOADING, payload: false });
    }
  };

  useEffect(() => {
    if (!showTemplateModal) return; // Only work when visible
    // If template param is missing (user navigated away to non-template route) while modal still logically open
    if (!template) {
      if (htmlContent) setHtmlContent("");
      invalidatedRef.current = true; // mark content as invalidated
      return; // nothing to load until a template slug returns
    }

    // If we return to a template (same or different) after invalidation, force remount & clear invalidation
    if (invalidatedRef.current) {
      setRefreshKey(k => k + 1);
      invalidatedRef.current = false;
    }

    // Clear stale content immediately if switching to a new template
    if (template && lastLoadedTemplateRef.current && lastLoadedTemplateRef.current !== template) {
      setHtmlContent("");
      setRefreshKey(k => k + 1); // force remount container
    }
    dispatch({ type: templateActionEnum.SET_IS_LOADING, payload: true });
    const cancelToken = axios.CancelToken.source();
    const config = { cancelToken: cancelToken.token };
    (async () => {
      try {
        await getTemplate(config);
      } catch (error) {
        if (!axios.isCancel(error)) {
          logger.error(
            "🚀 ~ file: ModalComponent.tsx ~ getTemplate ~ error",
            error
          );
          dispatch({
            type: templateActionEnum.SET_ERROR,
            payload: { show: true, message: ErrorMessage.TEMPLATE_HTML_FAILED },
          });
        }
      } finally {
        dispatch({ type: templateActionEnum.SET_IS_LOADING, payload: false });
      }
    })();
    return () => cancelToken.cancel();
  }, [template, selectedState, showTemplateModal, location.key]);

  const handleClose = () => {
    dispatch({ type: templateActionEnum.SET_MODAL_STATE, payload: false });
  };

  // Targeted normalization ONLY inside the deepest template wrapper so labels stay intact
  // while long bracket tokens wrap at underscores. This mirrors Review Summary behavior
  // without altering global CSS rules (.page-thumbs).
  useEffect(() => {
    if (!showTemplateModal || !htmlContent) return;
    // Defer until DOM nodes are painted
    const raf = requestAnimationFrame(() => {
      const root = modalContentRef.current?.querySelector('#getHtml') as HTMLElement | null;
      if (!root) return;
      // Try to select a wrapper whose class matches the template slug (e.g., .ground-lease-agreement)
      let target: HTMLElement | null = null;
      if (template) {
        target = root.querySelector('.' + CSS.escape(template)) as HTMLElement | null;
      }
      // Fallback: deepest child inside thumb-preview-wrap
      if (!target) {
        target = root.querySelector('.thumb-preview-wrap > div:last-child') as HTMLElement | null;
      }
      if (!target) return;

      // Restore natural word behavior for normal text; token wrapping handled via ZWSP insertion
      target.style.wordBreak = 'normal';
      target.style.overflowWrap = 'normal';
      target.style.hyphens = 'manual';

      const tokenRegex = /\[[A-Z0-9_]{8,}\]/g; // bracket tokens with underscores
      const labelRegex = /^(Landlord|Tenant|Seller|Buyer|Name|Date|Signature):?$/i;
      const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
      const toWrap: Text[] = [];
      while (walker.nextNode()) {
        const node = walker.currentNode as Text;
        if (!node.nodeValue) continue;
        const original = node.nodeValue;
        const trimmed = original.trim();
        if (!trimmed) continue;
        // Insert ZWSP after underscores ONLY inside bracket tokens to allow wrapping there
        if (tokenRegex.test(original)) {
          tokenRegex.lastIndex = 0;
            // Only change underscores that are between alphanumerics inside the token
          const replaced = original.replace(/_/g, '_\u200b');
          if (replaced !== original) node.nodeValue = replaced;
        }
        if (labelRegex.test(trimmed)) {
          toWrap.push(node);
        }
      }
      // Wrap labels in non-breaking span (avoid duplicates)
      toWrap.forEach(node => {
        const parent = node.parentElement;
        if (!parent) return;
        if (parent.classList.contains('prevent-line-break') || parent.closest('.prevent-line-break')) return;
        const full = node.nodeValue || '';
        const trimmed = full.trim();
        const leading = full.match(/^\s*/)?.[0] || '';
        const trailing = full.match(/\s*$/)?.[0] || '';
        const span = document.createElement('span');
        span.className = 'prevent-line-break';
        span.textContent = trimmed; // preserve colon if present
        const frag = document.createDocumentFragment();
        if (leading) frag.appendChild(document.createTextNode(leading));
        frag.appendChild(span);
        if (trailing) frag.appendChild(document.createTextNode(trailing));
        parent.replaceChild(frag, node);
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [showTemplateModal, htmlContent, template]);

  // Post-render normalization: similar behavior to Review Summary (independent wrapping)
  useEffect(() => {
    if (!showTemplateModal || !htmlContent || !modalContentRef.current) return;
    const root = modalContentRef.current;
    // 1. Insert zero-width spaces after underscores in long bracket tokens so they can wrap
    const tokenPattern = /\[[A-Z0-9_]{8,}\]/g;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    while (walker.nextNode()) {
      const n = walker.currentNode as Text;
      if (!n.nodeValue) continue;
      textNodes.push(n);
    }
    textNodes.forEach(n => {
      const val = n.nodeValue || '';
      if (tokenPattern.test(val)) {
        tokenPattern.lastIndex = 0;
        const replaced = val.replace(/_/g, '_\u200b');
        if (replaced !== val) n.nodeValue = replaced;
      }
    });
    // 2. Protect specific standalone labels from wrapping (Landlord/Tenant/Seller/Buyer/Name/Date/Signature)
    const labelPattern = /^(Landlord|Tenant|Seller|Buyer|Name|Date|Signature):?$/i;
    textNodes.forEach(n => {
      const trimmed = n.nodeValue?.trim();
      if (!trimmed || !labelPattern.test(trimmed)) return;
      const parent = n.parentElement;
      if (!parent) return;
      // Avoid double wrapping
      if (parent.classList.contains('prevent-line-break') || parent.closest('.prevent-line-break')) return;
      const span = document.createElement('span');
      span.className = 'prevent-line-break';
      span.textContent = trimmed;
      const original = n.nodeValue || '';
      const leading = original.match(/^\s*/)?.[0] || '';
      const trailing = original.match(/\s*$/)?.[0] || '';
      const frag = document.createDocumentFragment();
      if (leading) frag.appendChild(document.createTextNode(leading));
      frag.appendChild(span);
      if (trailing) frag.appendChild(document.createTextNode(trailing));
      parent.replaceChild(frag, n);
    });
    // 3. Reset word-breaking in this container so only tokens (with inserted ZWSP) wrap
    root.querySelectorAll('*').forEach(el => {
      (el as HTMLElement).style.wordBreak = 'normal';
      (el as HTMLElement).style.overflowWrap = 'normal';
      (el as HTMLElement).style.hyphens = 'manual';
    });
  }, [showTemplateModal, htmlContent]);

  return (
    <Modal
      className={
        template && htmlContent
          ? isModalClass + " modal-prev-temp"
          : "template-modal modal-prev-temp"
      }
      show={showTemplateModal}
      onHide={handleClose}
      centered
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title className="flex-grow-1">
          <ModalTitle title={title} changeLayout={changeLayout} />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-body">
        {isLoading && <LoaderComponent />}
        {!isLoading && template && !htmlContent && (
          <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 120 }}>
            <span className="text-muted small">Loading preview…</span>
          </div>
        )}
        {template && htmlContent && !isLoading && (
          <div
            key={refreshKey}
            style={{ maxHeight: '70vh', overflowY: 'auto' }}
            ref={modalContentRef}
          >
            <Template
              svgData={svgData}
              templateKey={template}
              htmlContent={htmlContent}
              isInnerPage
              templatePrev={modalContentRef}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer></Modal.Footer>
    </Modal>
  );
};

export default ModalComponent;
